import React, { useEffect, useState } from "react";
import { CalendarDays, Clock, ClipboardList, CreditCard, MessageSquare } from "lucide-react";
import { format, addDays } from "date-fns";
import { toast } from "sonner";
import { Card, Pill, PrimaryButton, EmptyState } from "../UI";
import { DatePicker } from "../DatePicker";
import { ChoiceSelect } from "../ChoiceSelect";
import portalApi, { errMsg } from "../../lib/portalApi";
import { OffersBanner, WaitlistCard } from "./WaitlistOffers";

const when = (s) => format(new Date(s), "EEE, MMM d · h:mm a");
const t12 = (t) => format(new Date(`2000-01-01T${t}`), "h:mm a");

export const OverviewTab = ({ me, go }) => {
  const [appts, setAppts] = useState([]);
  const [summary, setSummary] = useState({ forms: 0, balance: 0 });
  const load = () => portalApi.get("/portal/appointments").then(({ data }) => setAppts(data));
  useEffect(() => {
    load();
    Promise.all([portalApi.get("/portal/forms"), portalApi.get("/portal/balance")]).then(([f, b]) =>
      setSummary({ forms: f.data.filter((x) => x.status === "pending").length, balance: b.data.total }));
  }, []);
  const upcoming = appts.filter((a) => a.status === "scheduled" && new Date(a.start_time) > new Date()).reverse();
  const cancel = async (id) => {
    try { await portalApi.post(`/portal/appointments/${id}/cancel`); toast.success("Session cancelled"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const tiles = [
    { label: "Forms to complete", value: summary.forms, icon: ClipboardList, tab: "forms" },
    { label: "Balance due", value: `$${summary.balance.toFixed(2)}`, icon: CreditCard, tab: "pay" },
    { label: "Secure messages", value: "Open", icon: MessageSquare, tab: "messages" },
  ];
  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-serif text-4xl sm:text-5xl mb-1" data-testid="portal-greeting">Hi, {me.first_name}.</h1>
        <p className="text-sm" style={{ color: "var(--mf-text-muted)" }}>Your care with {me.clinician_name || "your clinician"}, all in one calm place.</p>
      </div>
      <OffersBanner onClaimed={load} />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {tiles.map((t, i) => (
          <button key={t.label} onClick={() => go(t.tab)} className="mf-card p-5 text-left mf-fade-in hover:-translate-y-0.5 transition-transform" style={{ animationDelay: `${i * 0.05}s` }} data-testid={`portal-tile-${t.tab}`}>
            <t.icon className="w-5 h-5 mb-3" style={{ color: "var(--mf-accent)" }} />
            <div className="font-serif text-2xl">{t.value}</div>
            <div className="text-xs" style={{ color: "var(--mf-text-muted)" }}>{t.label}</div>
          </button>
        ))}
      </div>
      <Card testid="portal-upcoming">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-xl">Upcoming sessions</h3>
          <PrimaryButton onClick={() => go("book")} testid="portal-book-cta">Book a session</PrimaryButton>
        </div>
        {upcoming.length === 0 ? <EmptyState icon={CalendarDays} title="No upcoming sessions" description="Book your next session in a few taps." /> : (
          <div className="space-y-2">
            {upcoming.map((a) => (
              <div key={a.id} className="p-3 rounded-xl flex flex-wrap items-center gap-3" style={{ background: "var(--mf-muted)" }} data-testid={`portal-appt-${a.id}`}>
                <Clock className="w-4 h-4" style={{ color: "var(--mf-primary)" }} />
                <span className="text-sm font-medium flex-1">{when(a.start_time)}</span>
                <Pill variant="primary">{a.appointment_type}</Pill>
                <button onClick={() => cancel(a.id)} className="text-xs px-3 py-1 rounded-full" style={{ background: "rgba(220,38,38,0.1)", color: "#b91c1c" }} data-testid={`portal-cancel-${a.id}`}>Cancel</button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export const BookTab = ({ go }) => {
  const [date, setDate] = useState(format(addDays(new Date(), 1), "yyyy-MM-dd"));
  const [slots, setSlots] = useState(null);
  const [chosen, setChosen] = useState("");
  const [type, setType] = useState("individual");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setSlots(null); setChosen("");
    portalApi.get("/portal/slots", { params: { date } }).then(({ data }) => setSlots(data.slots)).catch(() => setSlots([]));
  }, [date]);
  const book = async () => {
    setBusy(true);
    try {
      await portalApi.post("/portal/appointments", { date, time: chosen, appointment_type: type });
      toast.success("Session booked — confirmation sent to your email");
      go("home");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <>
    <OffersBanner onClaimed={() => go("home")} />
    <Card testid="portal-book">
      <h3 className="font-serif text-2xl mb-1">Book a session</h3>
      <p className="text-sm mb-5" style={{ color: "var(--mf-text-muted)" }}>Pick a day, then an open time. Times shown in your clinician's local time.</p>
      <div className="grid sm:grid-cols-2 gap-3 mb-5">
        <DatePicker value={date} onChange={(e) => setDate(e.target.value)} fromYear={new Date().getFullYear()} toYear={new Date().getFullYear() + 1}
          className="w-full px-4 py-2.5 rounded-xl text-sm" style={{ background: "var(--mf-muted)" }} data-testid="portal-book-date" />
        <ChoiceSelect value={type} onChange={setType} testid="portal-book-type"
          options={[{ value: "individual", label: "In-person session" }, { value: "telehealth", label: "Video session" }]} />
      </div>
      {slots === null ? <div className="mf-skeleton h-16" /> : slots.length === 0 ? (
        <div className="text-sm p-4 rounded-xl" style={{ background: "var(--mf-muted)" }} data-testid="portal-no-slots">No open times this day. Try another date.</div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid="portal-slots">
          {slots.map((s) => (
            <button key={s} onClick={() => setChosen(s)} className="py-2.5 rounded-xl text-sm font-medium transition-colors"
              style={{ background: chosen === s ? "var(--mf-primary)" : "var(--mf-muted)", color: chosen === s ? "#FAFAF8" : "var(--mf-text)" }} data-testid={`portal-slot-${s}`}>{t12(s)}</button>
          ))}
        </div>
      )}
      <div className="mt-6"><PrimaryButton onClick={book} disabled={!chosen || busy} testid="portal-book-submit">{busy ? "Booking…" : chosen ? `Book ${t12(chosen)}` : "Choose a time"}</PrimaryButton></div>
    </Card>
    <WaitlistCard />
    </>
  );
};
